import {
  accessoryFor,
  estimatedProfitForSku,
  internalProductCost,
  profitWithRebateFor,
  rebateAmountForSku,
  shippingProviderFor,
} from './productMaster'
import { skuMatchesTag, type ProductSegmentIndex } from './segments'
import type {
  InternalProductInfoRecord,
  ProductMasterDefaults,
  ProductMasterRecord,
  ProductRecord,
} from './types'

export interface SkuProfitResult {
  product: ProductRecord
  gmv: number
  profit: number | null
  profitWithRebate: number | null
  active: boolean
  covered: boolean
}

export interface ProductProfitSummary {
  profit: number | null
  profitWithRebate: number | null
  profitWithRebateMargin: number | null
  coveredGmv: number
  activeSkuCount: number
  coveredSkuCount: number
  context: string
}

export function internalProductRecordsByCode(records: InternalProductInfoRecord[]) {
  const index = new Map<string, InternalProductInfoRecord[]>()
  for (const record of records) {
    if (!record.productCode) continue
    const matches = index.get(record.productCode) || []
    matches.push(record)
    index.set(record.productCode, matches)
  }
  return index
}

export function calculateSkuProfit(
  product: ProductRecord,
  dates: string[],
  selectedDate: string,
  records: Map<string, ProductMasterRecord>,
  costDefaults: ProductMasterDefaults,
  companyRecordsByCode: Map<string, InternalProductInfoRecord[]>,
  segmentIndex: ProductSegmentIndex,
): SkuProfitResult {
  const record = records.get(product.id)
  const companyRecords = companyRecordsByCode.get(product.merchantSku.trim()) || []
  const isAccessory = accessoryFor(product, record, companyRecords)
  const internalCost = internalProductCost(companyRecords)
  const inheritedJdWarehouse = skuMatchesTag(segmentIndex, product.id, 'jd-warehouse', dates)
  const shippingProvider = shippingProviderFor(
    record,
    inheritedJdWarehouse,
    selectedDate,
    costDefaults,
    isAccessory,
    internalCost.isBrandSpecial,
  )
  const estimate = estimatedProfitForSku(
    product,
    record,
    dates,
    costDefaults,
    companyRecords,
    shippingProvider,
  )
  const rebateAmount = rebateAmountForSku(product, dates, companyRecords, isAccessory)
  const profitWithRebate = profitWithRebateFor(estimate.profit, rebateAmount)
  return {
    product,
    gmv: estimate.metrics.gmv,
    profit: estimate.profit,
    profitWithRebate,
    active: estimate.activeDates > 0,
    covered: estimate.profit !== null && profitWithRebate !== null,
  }
}

export function summarizeProductProfit(
  products: ProductRecord[],
  dates: string[],
  selectedDate: string,
  records: Map<string, ProductMasterRecord>,
  costDefaults: ProductMasterDefaults,
  companyRecordsByCode: Map<string, InternalProductInfoRecord[]>,
  segmentIndex: ProductSegmentIndex,
): ProductProfitSummary {
  const rows = products.map((product) => calculateSkuProfit(
    product,
    dates,
    selectedDate,
    records,
    costDefaults,
    companyRecordsByCode,
    segmentIndex,
  ))
  const activeRows = rows.filter((row) => row.active)
  const coveredRows = activeRows.filter((row) => row.covered)
  const profit = coveredRows.length
    ? coveredRows.reduce((sum, row) => sum + (row.profit || 0), 0)
    : null
  const profitWithRebate = coveredRows.length
    ? coveredRows.reduce((sum, row) => sum + (row.profitWithRebate || 0), 0)
    : null
  const coveredGmv = coveredRows.reduce((sum, row) => sum + row.gmv, 0)
  return {
    profit,
    profitWithRebate,
    profitWithRebateMargin: profitWithRebate !== null && coveredGmv > 0
      ? profitWithRebate / coveredGmv
      : null,
    coveredGmv,
    activeSkuCount: activeRows.length,
    coveredSkuCount: coveredRows.length,
    context: activeRows.length
      ? `已覆盖 ${coveredRows.length}/${activeRows.length} 个成交SKU`
      : '当前范围无可计算成交SKU',
  }
}
